// Shared URL handling for the lead forms.
//
// Both forms take links people paste from a phone, so the rules are deliberately
// forgiving: a missing protocol is normalized rather than rejected, and Google
// Maps is matched on host rather than on one hardcoded URL shape.

/**
 * Trims and adds a scheme when the user pasted a bare domain. Returns null for
 * an empty value so callers can send null instead of "".
 */
export const normalizeUrl = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const parseUrl = (raw: string): URL | null => {
  const normalized = normalizeUrl(raw);
  if (!normalized) return null;
  try {
    return new URL(normalized);
  } catch {
    return null;
  }
};

/** Any plausible http(s) page: a website, a Facebook page, an Instagram profile. */
export const isWebUrl = (raw: string): boolean => {
  const url = parseUrl(raw);
  if (!url) return false;
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  // Rejects "https://localhost" and typos with no TLD.
  return /^[^.]+(\.[^.]+)+$/.test(url.hostname);
};

/**
 * A Facebook page in any of the forms Facebook itself serves: vanity handles,
 * the `/p/Name-123` pages it gives businesses, `profile.php?id=`, and the m./
 * web. hosts a phone produces. Anything under the domain with a path is
 * accepted — the point is to catch a wrong site pasted by mistake, not to police
 * which kind of Facebook URL someone copied.
 */
export const isFacebookUrl = (raw: string): boolean => {
  const url = parseUrl(raw);
  if (!url) return false;

  const host = url.hostname.toLowerCase();
  const onFacebook =
    host === "facebook.com" ||
    host.endsWith(".facebook.com") ||
    host === "fb.com" ||
    host === "fb.me";

  // The bare domain is the site, not a page.
  return onFacebook && url.pathname !== "/";
};

/**
 * Google Maps hands out several link shapes and the one people actually use is
 * the phone share sheet's `maps.app.goo.gl` shortlink, which looks nothing like
 * google.com/maps. Matching the host set covers every form, including the
 * country domains a Romanian browser produces (google.ro/maps).
 */
export const isGoogleMapsUrl = (raw: string): boolean => {
  const url = parseUrl(raw);
  if (!url) return false;

  const host = url.hostname.toLowerCase().replace(/^www\./, "");

  // Share shortlinks and the mobile host carry no /maps path segment.
  if (host === "maps.app.goo.gl" || host === "maps.google.com") return true;
  if (host === "goo.gl") return url.pathname.startsWith("/maps");

  // google.com, google.ro, google.co.uk ...
  if (/^google(\.[a-z]{2,})+$/.test(host)) return url.pathname.startsWith("/maps");

  return false;
};
